import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/context/AuthContext";
import { useCrumbs } from "@/context/CrumbContext";
import { Archive, ArchiveRestore, BadgeCheck, Download, FilePen, FileText, History, Loader2, Mail, Search, X } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const KIND = [["all", "Tüm belgeler"], ["technical", "Teknik raporlar"], ["summary", "Sonuç özetleri"]];
const SIGN = [["all", "Hepsi"], ["signed", "İmzalı"], ["unsigned", "İmzasız"], ["archived", "Arşivlendi"]];

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function HistoryModal({ rows, title, onClose, onDownload }) {
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="history-modal">
      <div className="modal wide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><div><div className="eyebrow">REVİZYON GEÇMİŞİ</div><h2>{title}</h2></div>
          <button className="icon-button" onClick={onClose} data-testid="history-close"><X size={16}/></button></div>
        <div className="history-list">
          {rows.map((r) => (
            <div key={r.id} className="history-row" data-testid={`history-row-${r.id}`}>
              <b className="mono">{r.code}</b>
              <div><strong>{r.kind === "summary" ? "Sonuç özeti" : "Teknik rapor"}</strong><small>{fmt(r.created_at)} · {r.created_by || "—"}{r.signed ? ` · İmzalı (${r.signatures.join(", ")})` : ""}{r.sent_to ? ` · Gönderildi: ${r.sent_to}` : ""}</small></div>
              <button className="secondary-btn" onClick={() => onDownload(r)} data-testid={`history-download-${r.id}`}><Download size={13}/> İndir</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ReportArchive() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [sign, setSign] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [history, setHistory] = useState(null);
  const [toast, setToast] = useState("");
  useCrumbs([{ label: "Rapor Arşivi", to: "/reports" }]);

  const load = () => apiClient.get("/reports/archive").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 2600); return () => clearTimeout(t); }, [toast]);

  const shown = useMemo(() => (rows || []).filter((r) => {
    const text = `${r.code} ${r.assessment_title} ${r.building_name} ${r.project_name} ${r.city} ${r.verify_code || ""} ${r.sent_to || ""}`.toLocaleLowerCase("tr");
    if (q && !text.includes(q.toLocaleLowerCase("tr"))) return false;
    if (kind !== "all" && r.kind !== kind) return false;
    if (sign === "signed" && !r.signed) return false;
    if (sign === "unsigned" && (r.signed || r.kind !== "technical")) return false;
    if (sign === "archived" && !r.archived) return false;
    const day = (r.created_at || "").slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  }), [rows, q, kind, sign, from, to]);

  const download = async (r) => {
    try {
      const url = r.kind === "summary" ? `/summaries/${r.id}/download` : `/reports/${r.id}/download`;
      const res = await apiClient.get(url, { responseType: "blob" });
      saveBlob(res.data, `hazmatcore-${r.kind === "summary" ? "sonuc-ozeti" : "rapor"}-${r.code}.pdf`);
    } catch { setToast("Belge indirilemedi"); }
  };

  const toggleArchive = async (r) => {
    try {
      await apiClient.patch(`/${r.kind === "summary" ? "summaries" : "reports"}/${r.id}/archive`, { archived: !r.archived });
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, archived: !r.archived } : x));
      setToast(r.archived ? "Arşivlendi etiketi kaldırıldı" : "Arşivlendi olarak işaretlendi (silinmez, listede kalır)");
    } catch { setToast("İşlem kaydedilemedi"); }
  };

  const openHistory = (r) => setHistory({ title: r.assessment_title, rows: rows.filter((x) => x.assessment_id === r.assessment_id) });

  return (
    <div className="content-wrap" data-testid="report-archive-page">
      <div className="page-heading">
        <div><div className="eyebrow">ARŞİV · HİÇBİR BELGE SİLİNMEZ</div><h1>Rapor Arşivi</h1>
          <p>{rows ? `${rows.length} belge · ${rows.filter((r) => r.kind === "technical").length} teknik rapor · ${rows.filter((r) => r.kind === "summary").length} sonuç özeti` : "Yükleniyor…"}</p></div>
      </div>
      <div className="list-toolbar archive-toolbar">
        <label className="search-box"><Search size={14}/><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bina, proje, şehir, rapor kodu veya doğrulama kodu" data-testid="archive-search"/></label>
        <div className="filter-row">{KIND.map(([k, l]) => <button key={k} className={kind === k ? "chip active" : "chip"} onClick={() => setKind(k)} data-testid={`archive-kind-${k}`}>{l}</button>)}</div>
        <div className="filter-row">{SIGN.map(([k, l]) => <button key={k} className={sign === k ? "chip active" : "chip"} onClick={() => setSign(k)} data-testid={`archive-sign-${k}`}>{l}</button>)}</div>
        <div className="date-range">
          <label>Başlangıç<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="archive-from"/></label>
          <label>Bitiş<input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="archive-to"/></label>
        </div>
      </div>
      {!rows ? <div className="page-loading"><Loader2 className="spin" size={18}/></div> : (
        <div className="data-table archive-table" data-testid="archive-table">
          <div className="data-head"><span>Belge</span><span>Değerlendirme / Bina</span><span>Tarih</span><span>Durum</span><span>İşlemler</span></div>
          {shown.map((r) => (
            <div key={r.id} className={`data-row${r.archived ? " is-archived" : ""}`} data-testid={`archive-row-${r.id}`}>
              <div className="doc-cell">
                {r.kind === "summary" ? <Mail size={16}/> : <FileText size={16}/>}
                <div><strong className="mono">{r.code}</strong><small>{r.kind === "summary" ? "Sonuç özeti" : "Teknik rapor"}</small></div>
              </div>
              <div><strong>{r.assessment_title}</strong><small>{r.building_name} · {r.city || "—"}</small></div>
              <span>{fmt(r.created_at)}<small>{r.created_by}</small></span>
              <span className="status-pills">
                {r.kind === "technical" && (r.signed ? <i className="pill ok"><BadgeCheck size={11}/> İmzalı</i> : <i className="pill warn">İmzasız</i>)}
                {r.kind === "summary" && r.verdict && <i className={`pill ${r.verdict.level === "pass" ? "ok" : r.verdict.level === "fail" ? "bad" : "warn"}`}>{r.verdict.title}</i>}
                {r.sent_to && <i className="pill">→ {r.sent_to}</i>}
                {r.archived && <i className="pill archived">Arşivlendi</i>}
              </span>
              <span className="row-actions">
                <button className="icon-button" title="İndir" onClick={() => download(r)} data-testid={`archive-download-${r.id}`}><Download size={14}/></button>
                <button className="icon-button" title="Revizyon geçmişi" onClick={() => openHistory(r)} data-testid={`archive-history-${r.id}`}><History size={14}/></button>
                <button className="icon-button" title="Revize et (yeni revizyon üret)" onClick={() => navigate(`/assessments/${r.assessment_id}?tab=findings`)} data-testid={`archive-revise-${r.id}`}><FilePen size={14}/></button>
                {r.verify_code && <a className="icon-button" title="İmza doğrulama" href={`/verify/${r.verify_code}`} target="_blank" rel="noreferrer" data-testid={`archive-verify-${r.id}`}><BadgeCheck size={14}/></a>}
                <button className="icon-button" title={r.archived ? "Arşivlendi etiketini kaldır" : "Arşivlendi olarak işaretle"} onClick={() => toggleArchive(r)} data-testid={`archive-flag-${r.id}`}>{r.archived ? <ArchiveRestore size={14}/> : <Archive size={14}/>}</button>
              </span>
            </div>
          ))}
          {!shown.length && <div className="data-empty">Bu filtrelere uyan belge yok.</div>}
        </div>
      )}
      {history && <HistoryModal {...history} onClose={() => setHistory(null)} onDownload={download}/>}
      {toast && <div className="toast" data-testid="archive-toast">{toast}</div>}
    </div>
  );
}
